import React, { useMemo, useState } from 'react';
import { getErrorMessage } from '../../utils/toast';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { EditOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import i18n, { datePickerFormat, formatDate } from '../../i18n';
import { useCreateSimEventMutation, useListSimEventsQuery } from '../../api/simulationApi';
import type { SimEvent, StateCorrectionMetric } from '../../types/simulation';

const { Text, Paragraph } = Typography;

/**
 * Коррекции показателей (backend docs/RFC_STATE_CORRECTIONS.md): событие
 * `state_correction` — «на день D значение было X». Точка на графике за D
 * равна введённому числу, D+1 считается от него; дата ≤ курсора — ретро
 * (энергия, гейт реального режима — как у полива). Температура почвы —
 * замер: расчёт частично подтягивается к нему и без новых замеров затухает.
 */

export type CorrectionTargetKind = 'planting' | 'bed';

export interface CorrectionTarget {
  kind: CorrectionTargetKind;
  id: string;
  label: string;
}

interface MetricMeta {
  code: StateCorrectionMetric;
  target: CorrectionTargetKind;
  min: number;
  max: number;
  step: number;
  /** Есть пояснение `saves:corrections.metrics.<code>.hint`. */
  hint?: boolean;
}

export const CORRECTION_METRICS: MetricMeta[] = [
  { code: 'health_pct', target: 'planting', min: 0, max: 100, step: 1 },
  { code: 'height_cm', target: 'planting', min: 0, max: 1000, step: 1 },
  { code: 'soil_moisture_pct', target: 'bed', min: 0, max: 100, step: 1, hint: true },
  { code: 'soil_temp_c', target: 'bed', min: -30, max: 60, step: 0.5, hint: true },
  { code: 'soil_ph', target: 'bed', min: 3, max: 10, step: 0.1 },
  { code: 'soil_nitrogen', target: 'bed', min: 0, max: 100, step: 1 },
  { code: 'soil_phosphorus', target: 'bed', min: 0, max: 100, step: 1 },
  { code: 'soil_potassium', target: 'bed', min: 0, max: 100, step: 1 },
];

const isKnownMetric = (code: string) => CORRECTION_METRICS.some((m) => m.code === code);
const metricLabel = (code: string): string =>
  (isKnownMetric(code) ? i18n.t(`saves:corrections.metrics.${code}.label`) : code);
const metricUnit = (code: string): string =>
  (isKnownMetric(code) ? i18n.t(`saves:corrections.metrics.${code}.unit`) : '');
const metricHint = (code: string): string => i18n.t(`saves:corrections.metrics.${code}.hint`);

interface FormValues {
  targetKey: string;
  metric: StateCorrectionMetric;
  value?: number;
  date: Dayjs;
  notes?: string;
}

interface StateCorrectionsPanelProps {
  saveId: string;
  sessionId?: string | null;
  /** Все цели, которые можно править с этой страницы. */
  targets: CorrectionTarget[];
  /** Дата курсора сейва: подставляется в форму по умолчанию. */
  cursorDate?: string | null;
  /**
   * Фильтры списка коррекций (до двух: посадка и её грядка на странице посадки);
   * без фильтров — все события сейва.
   */
  listFilters?: Array<{ plantingId?: string; bedId?: string }>;
  title?: string;
}

const targetKey = (t: CorrectionTarget) => `${t.kind}:${t.id}`;

const eventCorrections = (ev: SimEvent): Record<string, number> => {
  const payload = (ev as unknown as { payload?: { corrections?: Record<string, number> } }).payload;
  return payload?.corrections && typeof payload.corrections === 'object' ? payload.corrections : {};
};

export const StateCorrectionsPanel: React.FC<StateCorrectionsPanelProps> = ({
  saveId, sessionId, targets, cursorDate, listFilters, title: titleProp,
}) => {
  const { t: tr } = useTranslation('saves');
  const title = titleProp ?? tr('corrections.title');
  const [form] = Form.useForm<FormValues>();
  const [showForm, setShowForm] = useState(false);
  const [createSimEvent, { isLoading: creating }] = useCreateSimEventMutation();

  // Бэкенд фильтрует по одной цели за запрос: посадка и её грядка — два запроса.
  const primaryFilter = listFilters?.[0] ?? {};
  const secondaryFilter = listFilters?.[1] ?? null;
  const primary = useListSimEventsQuery(
    { saveId, sessionId: sessionId || undefined, ...primaryFilter },
    { skip: !saveId },
  );
  const secondary = useListSimEventsQuery(
    { saveId, sessionId: sessionId || undefined, ...(secondaryFilter || {}) },
    { skip: !saveId || !secondaryFilter },
  );
  const isLoading = primary.isLoading || secondary.isLoading;
  const isError = primary.isError || secondary.isError;
  const corrections = useMemo(() => {
    const seen = new Set<string>();
    const merged: SimEvent[] = [];
    for (const ev of [...(primary.data?.events || []), ...(secondary.data?.events || [])]) {
      if (ev.action !== 'state_correction' || seen.has(ev.id)) continue;
      seen.add(ev.id);
      merged.push(ev);
    }
    return merged.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [primary.data, secondary.data]);

  const selectedTargetKey = Form.useWatch('targetKey', form);
  const selectedTarget = targets.find((t) => targetKey(t) === selectedTargetKey) || targets[0] || null;
  const selectedMetric = Form.useWatch('metric', form);
  const metricsForTarget = CORRECTION_METRICS.filter((m) => m.target === (selectedTarget?.kind ?? 'planting'));
  const metricMeta = metricsForTarget.find((m) => m.code === selectedMetric) || metricsForTarget[0];

  const handleTargetChange = (key: string) => {
    const t = targets.find((x) => targetKey(x) === key);
    const first = CORRECTION_METRICS.find((m) => m.target === (t?.kind ?? 'planting'));
    form.setFieldsValue({ metric: first?.code, value: undefined });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const target = targets.find((t) => targetKey(t) === values.targetKey);
      const meta = CORRECTION_METRICS.find((m) => m.code === values.metric);
      if (!target || !meta) return;
      const value = Number(values.value);
      if (!Number.isFinite(value)) {
        message.error(tr('corrections.errors.numberRequired'));
        return;
      }
      await createSimEvent({
        saveId,
        sessionId: sessionId || undefined,
        action: 'state_correction',
        date: values.date.format('YYYY-MM-DD'),
        ...(target.kind === 'planting' ? { plantingId: target.id } : { bedId: target.id }),
        corrections: { [meta.code]: value },
        notes: values.notes?.trim() || undefined,
      }).unwrap();
      message.success(tr('corrections.saved'));
      form.resetFields(['value', 'notes']);
      setShowForm(false);
    } catch (err) {
      const e = err as { data?: { message?: string; errorCode?: string }; errorFields?: unknown; status?: number };
      if (e?.errorFields) return;
      const msg = e?.data?.message ? getErrorMessage(e) : undefined;
      if (e.status === 402) message.error(msg || tr('corrections.errors.noEnergy'));
      else if (e.status === 403) message.error(msg || tr('corrections.errors.pastUnavailable'));
      else if (e.status === 429) message.error(msg || tr('corrections.errors.tooMany'));
      else message.error(msg || tr('corrections.errors.saveFailed'));
    }
  };

  const targetLabel = (ev: SimEvent): string => {
    const id = ev.plantingId || ev.planting_id || ev.bedId || ev.bed_id || null;
    const t = targets.find((x) => x.id === id);
    return t ? t.label : id ? `${id.slice(0, 8)}…` : '—';
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <EditOutlined /> {title}
          <Tooltip title={tr('corrections.tooltip')}>
            <InfoCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      }
      extra={
        <Button
          size="small"
          type={showForm ? 'default' : 'primary'}
          icon={<PlusOutlined />}
          disabled={targets.length === 0}
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? tr('corrections.hideForm') : tr('corrections.correct')}
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      {targets.length === 0 ? (
        <Alert type="info" showIcon message={tr('corrections.noTargets')} style={{ marginBottom: 8 }} />
      ) : null}

      {showForm && targets.length > 0 ? (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            targetKey: targetKey(targets[0]),
            metric: CORRECTION_METRICS.find((m) => m.target === targets[0].kind)?.code,
            // Открытый день: курсор уже посчитан, правка действует с утра следующего.
            date: cursorDate ? dayjs(cursorDate).add(1, 'day') : dayjs(),
          }}
          style={{ marginBottom: 16 }}
        >
          <Space wrap align="start" style={{ width: '100%' }}>
            <Form.Item name="targetKey" label={tr('corrections.form.target')} rules={[{ required: true }]} style={{ minWidth: 220 }}>
              <Select
                options={targets.map((t) => ({ value: targetKey(t), label: tr(t.kind === 'bed' ? 'corrections.form.bedOption' : 'corrections.form.plantingOption', { name: t.label }) }))}
                onChange={handleTargetChange}
              />
            </Form.Item>
            <Form.Item name="metric" label={tr('corrections.form.metric')} rules={[{ required: true }]} style={{ minWidth: 220 }}>
              <Select options={metricsForTarget.map((m) => ({ value: m.code, label: tr('corrections.form.metricOption', { label: metricLabel(m.code), unit: metricUnit(m.code) }) }))} />
            </Form.Item>
            <Form.Item
              name="value"
              label={metricMeta ? tr('corrections.form.valueWithUnit', { unit: metricUnit(metricMeta.code) }) : tr('corrections.form.value')}
              rules={[{ required: true, message: tr('corrections.form.valueRequired') }]}
              style={{ minWidth: 160 }}
            >
              <InputNumber style={{ width: '100%' }} min={metricMeta?.min} max={metricMeta?.max} step={metricMeta?.step ?? 1} />
            </Form.Item>
            <Form.Item
              name="date"
              label={tr('corrections.form.day')}
              rules={[{ required: true }]}
              style={{ minWidth: 160 }}
              extra={cursorDate ? tr('corrections.form.closedDays') : undefined}
            >
              {/* Коррекция на день курсора и раньше отвергается сервером
                  (STATE_CORRECTION_PAST_DATE): эти дни уже посчитаны, их пересчёт
                  переигрывает исполненные события (backend docs/RFC_OPEN_DAY_ACTIONS.md). */}
              <DatePicker
                style={{ width: '100%' }}
                format={datePickerFormat()}
                disabledDate={(d) => (cursorDate ? !d.isAfter(dayjs(cursorDate), 'day') : false)}
              />
            </Form.Item>
            <Form.Item name="notes" label={tr('corrections.form.note')} style={{ minWidth: 220 }}>
              <Input placeholder={tr('corrections.form.notePlaceholder')} maxLength={200} />
            </Form.Item>
          </Space>
          {metricMeta?.hint ? (
            <Alert type="info" showIcon message={metricHint(metricMeta.code)} style={{ marginBottom: 12 }} />
          ) : null}
          <Space>
            <Button type="primary" loading={creating} onClick={handleSubmit}>
              {tr('corrections.form.submit')}
            </Button>
            <Button onClick={() => setShowForm(false)}>{tr('common:actions.cancel')}</Button>
          </Space>
        </Form>
      ) : null}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      ) : isError ? (
        <Alert type="error" showIcon message={tr('corrections.loadFailed')} />
      ) : corrections.length === 0 ? (
        <Empty description={tr('corrections.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          size="small"
          dataSource={corrections}
          rowKey={(r) => r.id}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          columns={[
            {
              title: tr('corrections.form.day'),
              dataIndex: 'date',
              key: 'date',
              width: 110,
              render: (v: string) => (v ? formatDate(v.slice(0, 10), 'short') : '—'),
            },
            { title: tr('corrections.columns.target'), key: 'target', render: (_, ev) => targetLabel(ev) },
            {
              title: tr('corrections.columns.metrics'),
              key: 'corrections',
              render: (_, ev) => (
                <Space size={4} wrap>
                  {Object.entries(eventCorrections(ev)).map(([code, value]) => (
                    <Tag key={code}>
                      {metricLabel(code)}: <Text strong>{value}</Text> {metricUnit(code)}
                    </Tag>
                  ))}
                </Space>
              ),
            },
            {
              title: tr('corrections.form.note'),
              dataIndex: 'notes',
              key: 'notes',
              render: (v: string | null) => v || <Text type="secondary">—</Text>,
            },
          ]}
        />
      )}

      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
        {tr('corrections.footnote')}
      </Paragraph>
    </Card>
  );
};

export default StateCorrectionsPanel;
